import { Settings } from '@streamr/tv-engine/~/settings';
import { EventEmitter } from 'eventemitter3';
import { Timekeeper } from './Timekeeper';
import { Track } from './Track';
/**
 * Extracts audio frames from a MediaStreamTrack using MediaStreamTrackProcessor.
 * This is Chrome-only as MediaStreamTrackProcessor for audio is not supported in other browsers.
 */
export class ChromeAudioFrameExtractor {
    LOG_PREFIX = '[ChromeAudioFrameExtractor]';
    emitter = new EventEmitter();
    track;
    consumer;
    trackProcessor;
    abortController = new AbortController();
    stopped = false;
    outputTrack;
    // Metadata
    frameNumber = 0n;
    trackId;
    timestampOffsetMicros;
    constructor(track) {
        this.track = track;
        this.trackId = track.id;
        this.outputTrack = new Track(track.id, true);
    }
    start = () => {
        console.log(`${this.LOG_PREFIX} start called`, {
            trackId: this.trackId,
        });
        this.consumer = new WritableStream({
            write: (audioData) => {
                const unencodedAudioFrame = this.createUnencodedAudioFrame(audioData);
                if (this.outputTrack.hasListener()) {
                    this.outputTrack?.emit(unencodedAudioFrame, false, [
                        unencodedAudioFrame.data.buffer,
                    ]);
                }
                // AudioData must be closed after use
                audioData.close();
            },
        });
        this.trackProcessor = new MediaStreamTrackProcessor({
            track: this.track,
        });
        this.trackProcessor.readable
            .pipeTo(this.consumer, {
            signal: this.abortController.signal,
        })
            .catch((_e) => {
            console.log(`${this.LOG_PREFIX} pipeTo canceled`, {
                trackId: this.trackId,
            });
        })
            .finally(() => {
            this.stopped = true;
            console.log(`${this.LOG_PREFIX} pipeline stopped, emitting done`, {
                trackId: this.trackId,
            });
            this.outputTrack?.emit(undefined, true);
            this.emitter.emit('aborted');
        });
    };
    stop = () => {
        console.log(`${this.LOG_PREFIX} stop called`, {
            trackId: this.trackId,
        });
        if (this.stopped) {
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.abortController.abort('Aborted');
            this.emitter.once('aborted', () => {
                resolve();
            });
        });
    };
    getOutputTrack() {
        return this.outputTrack;
    }
    getTrackId() {
        return this.trackId;
    }
    /**
     * Create an UnencodedAudioFrame from AudioData.
     */
    createUnencodedAudioFrame(audioData) {
        if (!this.timestampOffsetMicros) {
            this.timestampOffsetMicros =
                Timekeeper.getInstance().getLastTimestampMicros();
        }
        // Extract audio samples from AudioData - always use first channel only
        const numberOfFrames = audioData.numberOfFrames;
        const samples = new Float32Array(numberOfFrames);
        audioData.copyTo(samples, { planeIndex: 0 });
        // Always use synthetic timestamps for consistency
        const timestampMicros = this.frameNumber * BigInt(Settings.audioFrameDurationMicroseconds) +
            this.timestampOffsetMicros;
        // Calculate duration from the number of frames and sample rate
        const durationMicros = BigInt(Math.round((numberOfFrames / audioData.sampleRate) * 1_000_000));
        Timekeeper.getInstance().setLastTimestampMicros(timestampMicros);
        const unencodedAudioFrame = {
            frameNumber: this.frameNumber++,
            timestamp: timestampMicros,
            duration: durationMicros,
            trackId: this.trackId,
            data: samples,
            done: false,
            isKeyFrame: true,
        };
        return unencodedAudioFrame;
    }
}
